import React, { useEffect, useState } from "react";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MESSAGE, isValidFileType } from "../../../../helpers/fileValidationRules";

const validationSchema = Yup.object().shape({
   serviceRecords: Yup.array().of(
      Yup.object().shape({
         odometerReading: Yup.number().required("Odometer Reading is required"),
         serviceDate: Yup.date().required("Service Date is required"),
         serviceAmount: Yup.number().required("Service Amount is required"),
         serviceStationName: Yup.string().required("Service Station Name is required"),
         serviceBillUpload: Yup.mixed()
            .required("Service Bill is required")
            .test("is-valid-type", "Not a valid document type", (value) =>
               isValidFileType(value && value.name.toLowerCase(), "all")
            )
            .test(
               "is-valid-size",
               MAX_FILE_SIZE_MESSAGE,
               (value) => value && value.size <= MAX_FILE_SIZE
            ),
      })
   ),
});

const initialValues = {
   serviceRecords: [
      {
         odometerReading: "",
         serviceDate: "",
         serviceAmount: "",
         serviceStationName: "",
         serviceBillUpload: null,
      },
   ],
};

function ServiceRecord({ setCurrentTabIndex, step3Data, setStep3Data }) {
   const [initialState, setInitialState] = useState(initialValues);

   const handleNext = (formData) => {
      setStep3Data(formData.serviceRecords);
      setCurrentTabIndex((prev) => prev + 1);
   };

   useEffect(() => {
      if (step3Data?.length > 0) {
         setInitialState((prev) => ({ serviceRecords: step3Data }));
      }
   }, []);

   const formik = useFormik({
      initialValues: initialState,
      validationSchema: validationSchema,
      enableReinitialize: true,
      onSubmit: handleNext,
   });

   const { errors, touched, values, getFieldProps, handleSubmit, setFieldValue } = formik;

   return (
      <FormikProvider value={formik}>
         <div
            className="tab-pane fade show active"
            id="2"
            role="tabpanel"
            aria-labelledby="2"
         >
            <div className="col-lg-12">
               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Service Record</h3>
                  </div>

                  <div className="card-body">
                     <FieldArray
                        name="serviceRecords"
                        render={(arrayHelpers) => (
                           <div>
                              {values.serviceRecords.map((item, index) => {
                                 return (
                                    <div
                                       className={
                                          index < values.serviceRecords.length - 1 &&
                                          values.serviceRecords.length > 1
                                             ? "row border-bottom mb-4 pb-3"
                                             : "row"
                                       }
                                    >
                                       <div className="col-lg-4">
                                          <CustomInput
                                             require
                                             inputType={"number"}
                                             label="Odometer reading"
                                             id="odometerReading"
                                             placeholder="Enter Amount"
                                             {...getFieldProps(
                                                `serviceRecords[${index}].odometerReading`
                                             )}
                                             errors={
                                                Boolean(
                                                   touched?.serviceRecords?.[index]
                                                      ?.odometerReading
                                                ) &&
                                                Boolean(
                                                   errors?.serviceRecords?.[index]
                                                      ?.odometerReading
                                                )
                                             }
                                             message={
                                                errors?.serviceRecords?.[index]
                                                   ?.odometerReading
                                             }
                                          />
                                       </div>
                                       <div className="col-lg-4">
                                          <CustomDatePicker
                                             require
                                             label="Service Date"
                                             id="serviceDate"
                                             placeholder="Select Service Date"
                                             {...getFieldProps(
                                                `serviceRecords[${index}].serviceDate`
                                             )}
                                             errors={
                                                Boolean(
                                                   touched?.serviceRecords?.[index]
                                                      ?.serviceDate
                                                ) &&
                                                Boolean(
                                                   errors?.serviceRecords?.[index]
                                                      ?.serviceDate
                                                )
                                             }
                                             message={
                                                errors?.serviceRecords?.[index]
                                                   ?.serviceDate
                                             }
                                          />
                                       </div>
                                       <div className="col-lg-4">
                                          <CustomInput
                                             require
                                             label="Service Amount"
                                             inputType={"number"}
                                             id="serviceAmount"
                                             placeholder="Enter Service Amount"
                                             name={"serviceAmount"}
                                             value={item.serviceAmount}
                                             {...getFieldProps(
                                                `serviceRecords[${index}].serviceAmount`
                                             )}
                                             errors={
                                                Boolean(
                                                   touched?.serviceRecords?.[index]
                                                      ?.serviceAmount
                                                ) &&
                                                Boolean(
                                                   errors?.serviceRecords?.[index]
                                                      ?.serviceAmount
                                                )
                                             }
                                             message={
                                                errors?.serviceRecords?.[index]
                                                   ?.serviceAmount
                                             }
                                          />
                                       </div>
                                       <div className="col-lg-4">
                                          <CustomInput
                                             require
                                             label="Service Station Name"
                                             id="serviceStationName"
                                             placeholder="Enter Service Station Name"
                                             {...getFieldProps(
                                                `serviceRecords[${index}].serviceStationName`
                                             )}
                                             errors={
                                                Boolean(
                                                   touched?.serviceRecords?.[index]
                                                      ?.serviceStationName
                                                ) &&
                                                Boolean(
                                                   errors?.serviceRecords?.[index]
                                                      ?.serviceStationName
                                                )
                                             }
                                             message={
                                                errors?.serviceRecords?.[index]
                                                   ?.serviceStationName
                                             }
                                          />
                                       </div>
                                       <div className="col-lg-4">
                                          <label className="text-bold">
                                             Service bill upload{" "}
                                             <span className="text-danger">*</span>
                                          </label>
                                          <CustomFileUpload
                                             accept=".jpg, .png, .jpeg, .csv, .xlsx, .pdf, .doc, .webp"
                                             // onChange={(event) => handleFileChange(event)}
                                             name={"serviceBillUpload"}
                                             value={item.serviceBillUpload}
                                             onChange={(e) => {
                                                setFieldValue(
                                                   `serviceRecords[${index}].serviceBillUpload`,
                                                   e.currentTarget.files[0]
                                                );
                                             }}
                                             errors={
                                                Boolean(
                                                   touched?.serviceRecords?.[index]
                                                      ?.serviceBillUpload
                                                ) &&
                                                Boolean(
                                                   errors?.serviceRecords?.[index]
                                                      ?.serviceBillUpload
                                                )
                                             }
                                             message={
                                                errors?.serviceRecords?.[index]
                                                   ?.serviceBillUpload
                                             }
                                          />
                                       </div>
                                       <div className="col-lg-4 mt-2 pt-2 mb-3">
                                          {values.serviceRecords.length > 1 && (
                                             <button
                                                type="button"
                                                className="btn btn-danger float-left ml-3"
                                                onClick={() => {
                                                   arrayHelpers.remove(index);
                                                }}
                                             >
                                                <i className="fas fa-trash"></i> Delete
                                                item
                                             </button>
                                          )}
                                       </div>
                                    </div>
                                 );
                              })}
                           </div>
                        )}
                     />

                     <div className="row mt-3">
                        <div className="col-6">
                           <button
                              type="button"
                              className="btn btn-primary float-left"
                              onClick={() => {
                                 setFieldValue("serviceRecords", [
                                    ...values.serviceRecords,
                                    {
                                       odometerReading: "",
                                       serviceDate: "",
                                       serviceAmount: "",
                                       serviceStationName: "",
                                       serviceBillUpload: null,
                                    },
                                 ]);
                              }}
                           >
                              <i className="fas fa-plus"></i> Add More
                           </button>
                        </div>
                     </div>
                  </div>

                  <div className="card-body">
                     <div
                        style={{
                           maxWidth: "min-content",
                           display: "flex",
                           marginLeft: "auto",
                        }}
                     >
                        <button
                           className="btn btn-secondary px-4 py-3"
                           type="button"
                           onClick={() => setCurrentTabIndex((prev) => prev - 1)}
                        >
                           <h6 className="mb-0 text-uppercase">Back</h6>
                        </button>
                        <button
                           className="btn btn-primary px-4 py-3 float-right ml-3"
                           type="button"
                           onClick={handleSubmit}
                        >
                           <h6 className="mb-0 text-uppercase">Next</h6>
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </FormikProvider>
   );
}

export default ServiceRecord;
